import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `idle` — nothing to do; the cached account matches the credential.
 * `refetching` — a swap refetch is in flight, or about to be. The caller should wait.
 * `failed` — the refetch settled and the account is *still* stale. The caller must show an error
 *   or a retry, and must not keep waiting.
 */
export type CredentialSwapStatus = "idle" | "refetching" | "failed";

/**
 * Refetches the account when another tab swaps the credential underneath it, and — the part that
 * matters — cannot get stuck doing so.
 *
 * The obvious shape of this is a latch: fire once when the account goes stale, swallow the
 * rejection. That is the defect `auth-guard.tsx` documents at length, and it was reintroduced here
 * in all three providers before this hook existed: because `isStale` stays `true` when the refetch
 * fails, the effect's own dependency never changes, nothing re-dispatches, and the wait branch
 * renders forever. A branch that returns `null` forever is a blank screen, which is the reported
 * symptom of B-1927.
 *
 * Two separate guards, and they prevent different things — the distinction the original latch got
 * wrong:
 *
 * - **`inFlight` prevents the storm.** It means "a swap refetch is in flight", never "the effect
 *   has run once". It is cleared on settle, success or failure, so it can never be spent on an
 *   attempt that achieved nothing.
 * - **`settledWhileStale` prevents the strand *and* the loop.** Clearing `inFlight` alone would
 *   re-dispatch on the next commit and spin forever, because a failing refetch leaves the account
 *   exactly as stale as it found it. Recording that an attempt has already settled without
 *   progress is what turns "wait" into "failed", so the caller falls through to its error surface
 *   instead of either hanging or hammering.
 *
 * Recovery resets it: the moment `isStale` goes false, a later swap gets a fresh attempt. So the
 * budget is one automatic attempt per swap, not one per mount.
 *
 * Lives in `@qw/auth` because all three providers need identical behaviour here and have already
 * diverged five times (B-1968). `retry` is exposed so a caller with a retry affordance can re-arm
 * deliberately; a caller without one just renders its error branch on `failed`.
 */
export function useCredentialSwapRefetch({ isStale, refetch }: { isStale: boolean; refetch: () => Promise<unknown> }): {
  status: CredentialSwapStatus;
  retry: () => void;
} {
  const inFlight = useRef(false);
  const [settledWhileStale, setSettledWhileStale] = useState(false);

  // Read through a ref so this hook's effect depends on the *staleness*, not on a callback
  // identity: Apollo rebuilds `refetch` on every render, and depending on it directly would
  // re-dispatch on every render for as long as the account stayed stale.
  const refetchRef = useRef(refetch);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!isStale) {
      setSettledWhileStale(false);
    }
  }, [isStale]);

  useEffect(() => {
    if (!isStale || inFlight.current || settledWhileStale) {
      return;
    }

    inFlight.current = true;

    void (async () => {
      try {
        await refetchRef.current();
      } catch {
        // The failure is already reported through the query's own `error`. All this handler owes
        // the caller is the fact that the attempt is over.
      } finally {
        inFlight.current = false;
        setSettledWhileStale(true);
      }
    })();
  }, [isStale, settledWhileStale]);

  const retry = useCallback(() => {
    setSettledWhileStale(false);
  }, []);

  if (!isStale) {
    return { retry, status: "idle" };
  }

  return { retry, status: settledWhileStale ? "failed" : "refetching" };
}
